import { ChangeDetectorRef, Component, computed, ElementRef, OnInit, signal, Signal, ViewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { AuthService } from '@services/auth/auth.service';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ToastService } from '@services/helper/toast.service';
import { HelperService } from '@services/helper/helper.service';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { AuthenticatedUser, DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { LoadingComponent } from '@components/loading/loading.component';
import { AdminService } from '@services/admin/admin.service';
import { CdkConnectedOverlay, CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import { AwTooltipComponent } from '@components/misc/aw-tooltip/aw-tooltip.component';
import { PaginatePipe } from '@pipes/paginate.pipe';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { AW } from '@airwallet/types';

@Component({
    selector: 'app-admin-sidebar',
    imports: [OverlayModule, FormsModule, TranslateModule, RouterLink, LoadingComponent, CdkOverlayOrigin, CdkConnectedOverlay, AwTooltipComponent, PaginatePipe, MatPaginator, MatPaginator],
    templateUrl: './admin-sidebar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './admin-sidebar.component.scss'
})
export class AdminSidebarComponent implements OnInit {
    @ViewChild('controlledInput') controlledInput!: ElementRef<HTMLInputElement>;
    isAdmin: Signal<boolean> = this.authService.isAdmin;

    dashboardOwnerUser: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();
    controlledUser: AW.ControlUid.ControlledDashboardOwnerUser | null = this.localStorageService.getItem('controlled_user') ?? null;
    authenticatedUser: AuthenticatedUser | null = this.localStorageService.getItem('authenticatedUser') ?? null;
    mfaEnrollments!: string;

    operatorUID: Signal<string> = computed(() => {
        return this.controlledUser?.uid.split('_operated_by_')[1] || 'N/A';
    });
    regionCountry: Signal<string> = computed(() => {
        if (!this.controlledUser?.billing?.shallow_account_details?.address) {
            return 'N/A';
        }
        const region: string = 'Region ' + this.controlledUser.settings.stripe_region?.toUpperCase();
        const country: string = this.helperService.getCountryTranslated(this.controlledUser.billing.shallow_account_details.address.country, true);
        return region + ' / ' + country;
    });

    isOnControlledLocation: WritableSignal<boolean> = signal(false);
    loadingControlHistory: WritableSignal<boolean> = signal(true);
    controlHistory: WritableSignal<AW.ControlUid.ControlHistoryEntry[]> = signal([]);
    crackingFortune: WritableSignal<boolean> = signal(false);
    pageSize = 10;
    pageNumber = 0;

    constructor(
        protected sidebarService: SidebarService,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private toastService: ToastService,
        private translate: TranslateService,
        protected helperService: HelperService,
        private featureService: FeatureService,
        protected adminService: AdminService,
        private cdr: ChangeDetectorRef
    ) {
        this.router.events.subscribe(event => {
            const controlledLocation = this.adminService.controlledLocation();
            if (event instanceof NavigationEnd && controlledLocation) {
                const isOnPage: boolean = event.urlAfterRedirects.includes(controlledLocation.search_key);
                if (!isOnPage) {
                    this.adminService.countOfTimesClicked = 0;
                }
                this.isOnControlledLocation.set(isOnPage);
            }
        });
    }

    async ngOnInit() {
        this.handleControlActive();
        this.authService.permissionsReady.then(async () => {
            if (this.isAdmin()) {
                this.loadingControlHistory.set(true);
                this.sidebarService.enableAdminSidebar();
                this.controlHistory.set(await this.adminService.getControlHistory());
                this.loadingControlHistory.set(false);
            }
        });
        if (this.controlledUser && this.controlledUser.enrolledFactors) {
            for (const enrollment of this.controlledUser.enrolledFactors) {
                const displayName = enrollment.displayName ?? '';
                this.mfaEnrollments = this.mfaEnrollments ? this.mfaEnrollments + ', ' + displayName : displayName;
            }
        }
    }

    async crackFortuneCookie() {
        if (this.crackingFortune()) return;
        this.crackingFortune.set(true);
        try {
            const { fortune } = await this.adminService.getFortune();
            this.toastService.success(fortune, 'Customer Fortune');
        } finally {
            this.crackingFortune.set(false);
        }
    }

    setControlHistory(open: boolean) {
        this.adminService.controlHistoryOpened.set(open);
    }

    handlePage(event: PageEvent) {
        this.pageNumber = event.pageIndex;
        this.pageSize = event.pageSize;
    }

    handleControlActive() {
        if (this.localStorageService.getItem('controlled_user') !== null) {
            this.sidebarService.enableAdminSidebar();
            this.adminService.controlActive.set(true);
            this.adminService.controlledUid.set(this.dashboardOwnerUser.uid);
        } else {
            this.adminService.controlActive.set(false);
        }
    }

    async exitControlledState() {
        if (!this.adminService.controlActive()) {
            this.toastService.warning("Bro.. You're not even controlling any user", 'Whoops..');
            return;
        }
        this.adminService.loadingControlledUid.set(true);
        this.adminService.controlledUid.set('');
        this.adminService.controlledLocation.set(null);
        this.resetLocalStorage();
        await this.router.navigateByUrl('/home', { skipLocationChange: false }).then(async result => {
            this.router.navigate([`/home`]).then(res => {
                setTimeout(() => {
                    this.adminService.loadingControlledUid.set(false);
                }, 500);
            });
        });
        this.adminService.controlActive.set(false);
        window.location.reload();
    }

    private resetLocalStorage() {
        this.localStorageService.removeItem('controlled_user');
        this.localStorageService.removeItem('controlled_location');
        this.localStorageService.removeItem('owner');
        this.localStorageService.setItem('authenticatedUser', this.localStorageService.getItem('admin_AuthenticatedUser') ?? undefined);
        this.localStorageService.setItem('dashboardOwnerUser', this.localStorageService.getItem('admin_DashboardOwnerUser') ?? undefined);
        this.localStorageService.setItem('featureAccess', this.localStorageService.getItem('admin_FeatureAccess') ?? undefined);
        this.localStorageService.setItem('featureAccessChecksum', this.localStorageService.getItem('admin_FeatureAccessChecksum') ?? undefined);
        this.localStorageService.setItem('subscriptionPlanType', this.localStorageService.getItem('admin_SubscriptionPlanType') ?? undefined);
        this.localStorageService.removeItem('admin_AuthenticatedUser');
        this.localStorageService.removeItem('admin_FeatureAccess');
        this.localStorageService.removeItem('admin_FeatureAccessChecksum');
        this.localStorageService.removeItem('admin_SubscriptionPlanType');
        this.localStorageService.removeItem('samlMembershipOwners');
        this.localStorageService.removeItem('returnUrl');
    }

    async enterControlledState(controlledUid: string) {
        this.adminService.controlHistoryOpened.set(false);
        this.controlledInput?.nativeElement.blur();

        if (!controlledUid) {
            this.toastService.info(this.translate.instant('navbar.customer_not_found'));
            return;
        }

        if (this.authenticatedUser?.uid === controlledUid) {
            this.toastService.info("You're already controlling this, silly", 'xD');
            return;
        }

        this.adminService.loadingControlledUid.set(true);

        if (this.localStorageService.getItem('admin_AuthenticatedUser')) {
            this.resetLocalStorage();
        }

        await this.authService
            .setControlledUid(controlledUid.replace(/\s/g, ''))
            .then(async () => {
                this.dashboardOwnerUser = this.helperService.getDashboardOwnerUser();
                if (this.helperService.compareUserToLocalStorage('controlled_user')) {
                    this.adminService.controlActive.set(true);
                    this.adminService.controlledUid.set(this.dashboardOwnerUser.uid);
                    window.location.reload();
                } else {
                    this.toastService.warning(this.translate.instant('Could not find anything to control'));
                    this.adminService.loadingControlledUid.set(false);
                    this.adminService.controlActive.set(false);
                }
            })
            .catch(() => {
                this.toastService.warning(this.translate.instant('Could not find anything to control'));
                this.adminService.loadingControlledUid.set(false);
            });
        this.featureService.setAccessMapFromLocalstorage();
        this.sidebarService.enableAdminSidebar();
    }

    async goToLocation() {
        const text = this.adminService.getRandomStringsForToast();
        if (this.isOnControlledLocation()) {
            const countClicked: number = this.adminService.countOfTimesClicked;
            if (countClicked < 9) this.toastService.warning(text, 'Oopsie..', { timeOut: 20000 });
            else this.toastService.error(text, countClicked === 10 ? 'Goodbye' : 'Stop it you fool', { timeOut: 10000 });
            if (countClicked === 10) {
                await new Promise(resolve => setTimeout(resolve, 2000));
                this.authService.signOut();
            }
        } else {
            this.adminService.countOfTimesClicked = 0;
            this.cdr.markForCheck();
            this.router.navigate([`/home`]).then(() => {
                this.router.navigateByUrl(`/locations/${this.adminService.controlledLocation()!.search_key}`);
            });
        }
    }
}
